import React, { useState, useRef, useCallback } from 'react';
import { 
  MicrophoneIcon, 
  StopIcon, 
  ExclamationTriangleIcon,
  ClockIcon 
} from '@heroicons/react/24/solid';

interface WebSpeechRecorderProps {
  onTranscriptionUpdate: (text: string, isFinal: boolean) => void;
  onError?: (error: string) => void;
  baseUrl: string;
  token: string;
  disabled?: boolean;
  className?: string;
}

interface RecordingState {
  status: 'idle' | 'recording' | 'processing' | 'error';
  error: string | null;
  interimText: string;
}

const WebSpeechRecorder: React.FC<WebSpeechRecorderProps> = ({
  onTranscriptionUpdate,
  onError,
  baseUrl,
  token,
  disabled = false,
  className = ''
}) => {
  const [state, setState] = useState<RecordingState>({
    status: 'idle',
    error: null,
    interimText: ''
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const startRecording = useCallback(async () => {
    try {
      setState({ status: 'recording', error: null, interimText: '' });
      
      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      
      streamRef.current = stream;
      audioChunksRef.current = [];

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        await processRecording();
      };

      mediaRecorder.start();
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      const errorMessage = `Failed to start recording: ${error}`;
      setState({ status: 'error', error: errorMessage, interimText: '' });
      onError?.(errorMessage);
    }
  }, [baseUrl, token, onTranscriptionUpdate, onError]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setState(prev => ({ ...prev, status: 'processing', interimText: 'Processing...' }));
    }
    
    // Stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const processRecording = useCallback(async () => {
    try {
      if (audioChunksRef.current.length === 0) {
        throw new Error('No audio data recorded');
      }

      // Create audio blob
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      console.log('🎵 Processing audio blob:', audioBlob.size, 'bytes');

      // Create form data
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      // Send to backend
      const response = await fetch(`${baseUrl}/api/v1/speech/transcribe-audio`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Transcription failed: ${response.status} - ${errorData}`);
      }

      const result = await response.json();
      console.log('✅ Transcription result:', result);

      if (result.success && result.text) {
        onTranscriptionUpdate(result.text, true);
        setState({ status: 'idle', error: null, interimText: '' });
      } else {
        throw new Error('Invalid transcription response');
      }

    } catch (error) {
      console.error('❌ Transcription error:', error);
      const errorMessage = `Transcription failed: ${error}`;
      setState({ status: 'error', error: errorMessage, interimText: '' });
      onError?.(errorMessage);
    }
  }, [baseUrl, token, onTranscriptionUpdate, onError]);

  const handlePress = useCallback(async () => {
    if (disabled) return;

    switch (state.status) {
      case 'idle':
        await startRecording();
        break;
      case 'recording':
        stopRecording();
        break;
      case 'error':
        setState({ status: 'idle', error: null, interimText: '' });
        break;
      default:
        break;
    }
  }, [disabled, state.status, startRecording, stopRecording]);

  // Get button classes based on status
  const getButtonClasses = () => {
    const baseClasses = 'w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200 theme-border-primary border-2';
    
    switch (state.status) {
      case 'recording':
        return `${baseClasses} bg-red-500 text-white animate-pulse shadow-lg`;
      case 'processing':
        return `${baseClasses} bg-healthy-green text-white opacity-75`;
      case 'error':
        return `${baseClasses} bg-red-500 text-white`;
      default:
        return `${baseClasses} bg-healthy-green hover:bg-green-600 text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg`;
    }
  };

  // Get button icon using Heroicons
  const getButtonIcon = () => {
    switch (state.status) {
      case 'recording':
        return <StopIcon className="w-8 h-8" />;
      case 'processing':
        return <ClockIcon className="w-8 h-8 animate-spin" />;
      case 'error':
        return <ExclamationTriangleIcon className="w-8 h-8" />;
      default:
        return <MicrophoneIcon className="w-8 h-8" />;
    }
  };

  // Get button text
  const getButtonText = () => {
    switch (state.status) {
      case 'recording':
        return 'Recording... Click to stop';
      case 'processing':
        return 'Processing...';
      case 'error':
        return 'Error - Click to retry';
      default:
        return 'Click to speak';
    }
  };

  const isActive = state.status === 'recording';
  const showTranscription = state.interimText && (state.status === 'processing');

  return (
    <div className={`flex flex-col items-center space-y-3 ${className}`}>
      {/* Interim Transcription Display - Only show during processing */}
      {showTranscription && (
        <div className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 max-w-xs">
          <p className="text-gray-600 dark:text-gray-300 text-sm text-center italic">
            {state.interimText}
          </p>
        </div>
      )}

      {/* Speech Button */}
      <div className="flex flex-col items-center space-y-3">
        <div className="relative">
          <button
            type="button"
            onClick={handlePress}
            disabled={disabled || state.status === 'processing'}
            className={getButtonClasses()}
            title={getButtonText()}
          >
            {/* Recording Indicator */}
            {isActive && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-ping"></div>
            )}
            
            {getButtonIcon()}
          </button>
        </div>

        {/* Status Text */}
        <p className={`text-sm text-center font-medium ${
          isActive ? 'theme-text-primary' : 
          state.status === 'error' ? 'text-red-600' : 
          'theme-text-secondary'
        }`}>
          {getButtonText()}
        </p>

        {/* Error Message */}
        {state.error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 max-w-xs">
            <p className="text-xs text-red-600 dark:text-red-400 text-center">
              {state.error}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebSpeechRecorder;